import { FC, FormEvent, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useISirklogMessagePublish,
  useISirkView,
} from "features/CenterContent/RoleContent/ISirk/useISirkData";
// eslint-disable-next-line drops/no-direct-spor-import
import { Field, Stack, Textarea } from "@vygruppen/spor-react";

import { useISirkLogMessages } from "features/CenterContent/RoleContent/ISirk/useISirkLogMessages";
import type { CreateIncidentLogMessageSchema } from "features/CenterContent/RoleContent/ISirk/types";
import { format } from "date-fns";
import { ActionCard } from "shared/components/drops/ActionCard";
import { ActionCardButton } from "shared/components/drops/ActionCardButton";
import { SpeechBubbleOutline24Icon } from "@vygruppen/spor-icon-react";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { compareDateStringAsc } from "shared/utils/dateTimeUtils";
import { LogEntry } from "features/CenterContent/VehicleDetails/shared/LogEntry";

type Props = {
  selectedDate: string;
  shiftId: string;
  operatorName?: string;
};

export const ISirkModuleForShifts: FC<Props> = ({
  selectedDate,
  shiftId,
  operatorName,
}) => {
  const { t } = useTranslation();
  const formId = useId();
  const { data } = useISirkView(selectedDate);

  const [logMessage, setLogMessage] = useState("");

  // Extract ISirk from shift number
  const isirkFromShift = shiftId.slice(0, 3);

  const filterIsirkBasedOnShift = data?.incidentNotes
    ?.filter((note) => note.isirk.endsWith(isirkFromShift))
    .map((note) => note.isirk);

  const iSirk = filterIsirkBasedOnShift?.toString();

  const { data: isirkMessage } = useISirkLogMessages(iSirk);

  const { postIncidentLog, mutationStatus } = useISirklogMessagePublish();

  const predefinedText = `For ${shiftId} / ${operatorName} / ${format(
    new Date(selectedDate),
    "dd.MM.yy",
  )}: `;

  const filteredLogs =
    isirkMessage?.incidentLogs?.filter((log) => {
      const msg = log.message ?? "";
      return msg.includes(shiftId) && msg.includes(operatorName ?? "");
    }) ?? [];

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedMessage = logMessage.trim();
    if (!trimmedMessage || !iSirk || mutationStatus === "pending") return;

    const payload: CreateIncidentLogMessageSchema = {
      message: `${predefinedText.trimEnd()} ${trimmedMessage}`,
      isirkId: iSirk,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload, {
      onSuccess: () =>
        setLogMessage((currentMessage) =>
          currentMessage === logMessage ? "" : currentMessage,
        ),
    });
  };

  return (
    <>
      <ActionCard
        title={t("Ny melding")}
        icon={<SpeechBubbleOutline24Icon aria-hidden="true" />}
        primaryAction={
          <ActionCardButton
            form={formId}
            type="submit"
            variant="primary"
            loading={mutationStatus === "pending"}
            disabled={
              !logMessage.trim() || !iSirk || mutationStatus === "pending"
            }
          >
            {t("Send til I-sirk-logg")}
          </ActionCardButton>
        }
      >
        <form id={formId} onSubmit={onSubmit}>
          <Stack align="stretch" gap="2">
            <Field w="100%">
              <Textarea
                label={t("Skriv loggmelding")}
                name="logmessage"
                value={logMessage}
                onChange={(event) => setLogMessage(event.target.value)}
                minHeight="7rem"
              />
            </Field>
          </Stack>
        </form>
      </ActionCard>
      {mutationStatus === "error" && (
        <FailureMessage
          customMessage={t(
            "Kunne ikke sende loggmelding. Prøv igjen, eller kontakt IT hvis feilen vedvarer.",
          )}
        />
      )}

      <Stack gap="0" align="stretch" mt={filteredLogs.length > 0 ? 2 : 0}>
        {filteredLogs
          .sort((a, b) => compareDateStringAsc(a.timestamp, b.timestamp))
          .map((log, index) => (
            <LogEntry
              key={log.id}
              timestamp={log.timestamp}
              message={
                log.message?.startsWith(predefinedText)
                  ? log.message.slice(predefinedText.length).trimStart()
                  : log.message
              }
              source={log.sourceSystem.author?.split("@")[0]}
              fallbackSource={log.sourceSystem.name}
              hideDashedLine={index === filteredLogs.length - 1}
            />
          ))}
      </Stack>
    </>
  );
};
